/**
 * Runtime public environment injection.
 *
 * Next.js App Router removed `publicRuntimeConfig` (Pages Router only), so
 * `NEXT_PUBLIC_*` variables read directly in client-side code are inlined at
 * BUILD time by webpack — the same built image cannot then serve prod and
 * dev with different hostnames/realms.
 *
 * This module renders a small `<script>` tag from a Server Component that
 * sets `window.__RUNTIME_CONFIG__` from the CONTAINER's env vars at request
 * time. Client-side code (see oidc/config.ts) reads that global first,
 * falling back to the build-time `NEXT_PUBLIC_*` value only for local dev
 * (`next dev` without a container). This lets one image tag promote from
 * dev to prod (see docs/v1/runbooks/dev-environment.md).
 *
 * @module @project-x/auth/runtime-env
 */

/** Global window property name holding the runtime-injected env snapshot. */
export const RUNTIME_CONFIG_GLOBAL = "__RUNTIME_CONFIG__";

/**
 * Public env var names that are safe to expose to the browser and that
 * differ between environments (dev vs prod) — the whole point of this
 * mechanism. Keep this list small and intentional; anything NOT listed here
 * still falls back to the normal build-time `NEXT_PUBLIC_*` inlining, which
 * is correct for values that are genuinely static (e.g. reCAPTCHA site key,
 * Google Maps keys — shared across environments).
 */
export const RUNTIME_PUBLIC_ENV_KEYS = [
  "NEXT_PUBLIC_PUBLIC_HOST",
  "NEXT_PUBLIC_PUBLIC_SCHEME",
  "NEXT_PUBLIC_KEYCLOAK_URL",
  "NEXT_PUBLIC_KEYCLOAK_REALM",
  "NEXT_PUBLIC_KEYCLOAK_CLIENT_ID",
  "NEXT_PUBLIC_API_BASE_URL",
  "NEXT_PUBLIC_EVENTS_URL",
  "NEXT_PUBLIC_ORDERS_URL",
  "NEXT_PUBLIC_USERS_URL",
  "NEXT_PUBLIC_SWAP_URL",
  "NEXT_PUBLIC_VALIDATOR_URL",
  "NEXT_PUBLIC_SHORTENER_PUBLIC_URL",
] as const;

export type RuntimePublicEnvKey = (typeof RUNTIME_PUBLIC_ENV_KEYS)[number];

/**
 * Reads the current values of RUNTIME_PUBLIC_ENV_KEYS from `process.env`.
 * Server-only — call from a Server Component or Route Handler, never from
 * client code (process.env is not meaningfully readable there anyway once
 * bundled, except for the build-time-inlined NEXT_PUBLIC_* subset).
 *
 * @returns A plain object with only the keys that have a non-empty value.
 */
export function buildRuntimeEnvSnapshot(): Partial<Record<RuntimePublicEnvKey, string>> {
  const snapshot: Partial<Record<RuntimePublicEnvKey, string>> = {};
  for (const key of RUNTIME_PUBLIC_ENV_KEYS) {
    const value = process.env[key];
    if (value && value.trim().length > 0) {
      snapshot[key] = value.trim();
    }
  }
  return snapshot;
}

/**
 * Serializes the runtime env snapshot as a safe inline `<script>` body that
 * assigns `window.__RUNTIME_CONFIG__`. JSON.stringify output is escaped to
 * prevent `</script>` breakout in the unlikely event an env value contains it.
 */
export function renderRuntimeEnvScriptBody(): string {
  const snapshot = buildRuntimeEnvSnapshot();
  const json = JSON.stringify(snapshot).replace(/</g, "\\u003c");
  return `window.${RUNTIME_CONFIG_GLOBAL} = ${json};`;
}

/**
 * Reads a single runtime-injected value on the client. Returns undefined
 * when running server-side, before hydration, or when the key was not
 * present in RUNTIME_PUBLIC_ENV_KEYS / not set in the container env.
 */
export function getRuntimeEnvValue(name: string): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const runtimeConfig = (window as unknown as Record<string, unknown>)[RUNTIME_CONFIG_GLOBAL] as
    | Record<string, string>
    | undefined;
  return runtimeConfig?.[name];
}
